import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { employments } from "@/lib/endpoints";
import { useApplicant } from "@/hooks/useApplicant";
import { useNestedList } from "@/hooks/useNestedList";
import { getFieldErrors, getFormError } from "@/lib/validation-errors";
import { Field, TextInput } from "@/components/Field";
import type {
  CreateEmploymentRequest,
  EmploymentResponse,
} from "@/lib/api-types";

/**
 * Step 3 — Employment history (Part 11 of N-400).
 * USCIS wants every job for the past 5 years, including unemployment,
 * student periods, retirement.
 */
export function StepEmployment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { applicant, isLoading: applicantLoading } = useApplicant(id);
  const list = useNestedList<EmploymentResponse, CreateEmploymentRequest>(
    "employments",
    employments,
    id,
    applicant?.id,
  );

  const [editing, setEditing] = useState<EmploymentResponse | "new" | null>(null);

  if (applicantLoading || list.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Employment</h1>
        <p className="text-sm text-slate-500 mt-1">
          List every job you&apos;ve had in the past 5 years. Include unemployment,
          student periods, and retirement.
        </p>
      </header>

      <ul className="space-y-3">
        {list.items.map((e) => (
          <li
            key={e.id}
            className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between"
          >
            <div className="text-sm">
              <div className="font-medium">
                {e.employer_name ??
                  (e.is_unemployed ? "Unemployed" : e.is_student ? "Student" : e.is_retired ? "Retired" : "Self-employed")}
              </div>
              <div className="text-slate-500">{e.occupation_title}</div>
              <div className="text-xs text-slate-400 mt-1">
                {e.start_date}
                {e.end_date ? ` → ${e.end_date}` : " → present"}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setEditing(e)}
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm("Delete this entry?")) list.remove.mutate(e.id);
                }}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>

      {!editing && (
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          + Add employment
        </button>
      )}

      {editing && (
        <EmploymentForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={async (req) => {
            if (editing === "new") await list.create.mutateAsync(req);
            else await list.update.mutateAsync({ id: editing.id, req });
            setEditing(null);
          }}
          saving={list.create.isPending || list.update.isPending}
          error={editing === "new" ? list.create.error : list.update.error}
        />
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/addresses`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Addresses
        </Link>
        <button
          type="button"
          onClick={() => navigate(`/cases/${id}/wizard/travel`)}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800"
        >
          Save & continue
        </button>
      </div>
    </div>
  );
}

function EmploymentForm({
  initial,
  onCancel,
  onSubmit,
  saving,
  error,
}: {
  initial: EmploymentResponse | null;
  onCancel: () => void;
  onSubmit: (req: CreateEmploymentRequest) => Promise<void>;
  saving: boolean;
  error?: unknown;
}) {
  const [form, setForm] = useState<CreateEmploymentRequest>({
    employer_name: initial?.employer_name ?? "",
    occupation_title: initial?.occupation_title ?? "",
    employer_city: initial?.employer_city ?? "",
    employer_state: initial?.employer_state ?? "",
    employer_country: initial?.employer_country ?? "",
    start_date: initial?.start_date ?? "",
    end_date: initial?.end_date ?? "",
    is_self_employed: initial?.is_self_employed ?? false,
    is_unemployed: initial?.is_unemployed ?? false,
    is_student: initial?.is_student ?? false,
    is_retired: initial?.is_retired ?? false,
  });

  const set = <K extends keyof CreateEmploymentRequest>(
    k: K,
    v: CreateEmploymentRequest[K],
  ) => setForm((s) => ({ ...s, [k]: v }));

  const fieldErrors = getFieldErrors(error);
  const formError = getFormError(error);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const clean: CreateEmploymentRequest = { ...form };
        // Drop empty optional strings.
        (Object.keys(clean) as (keyof CreateEmploymentRequest)[]).forEach((k) => {
          if (clean[k] === "") (clean as Record<string, unknown>)[k] = undefined;
        });
        void onSubmit(clean);
      }}
      className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4"
    >
      <h3 className="font-semibold">{initial ? "Edit" : "Add employment"}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Employer / school name"
          className="sm:col-span-2"
          error={fieldErrors?.["employer_name"]?.message}
        >
          <TextInput
            value={form.employer_name ?? ""}
            onChange={(e) => set("employer_name", e.target.value)}
          />
        </Field>
        <Field label="Job title" error={fieldErrors?.["occupation_title"]?.message}>
          <TextInput
            value={form.occupation_title ?? ""}
            onChange={(e) => set("occupation_title", e.target.value)}
          />
        </Field>
        <Field label="City" error={fieldErrors?.["employer_city"]?.message}>
          <TextInput
            value={form.employer_city ?? ""}
            onChange={(e) => set("employer_city", e.target.value)}
          />
        </Field>
        <Field
          label="State"
          error={fieldErrors?.["employer_state"]?.message}
        >
          <TextInput
            value={form.employer_state ?? ""}
            onChange={(e) => set("employer_state", e.target.value)}
          />
        </Field>
        <Field
          label="Country"
          error={fieldErrors?.["employer_country"]?.message}
        >
          <TextInput
            value={form.employer_country ?? ""}
            onChange={(e) => set("employer_country", e.target.value)}
          />
        </Field>
        <Field label="Start date" required error={fieldErrors?.["start_date"]?.message}>
          <TextInput
            type="date"
            value={form.start_date ?? ""}
            onChange={(e) => set("start_date", e.target.value)}
          />
        </Field>
        <Field
          label="End date"
          hint="Leave blank if currently here"
          error={fieldErrors?.["end_date"]?.message}
        >
          <TextInput
            type="date"
            value={form.end_date ?? ""}
            onChange={(e) => set("end_date", e.target.value)}
          />
        </Field>
      </div>

      <fieldset className="text-sm">
        <legend className="font-medium">Status</legend>
        <div className="flex flex-wrap gap-4 mt-2 text-slate-700">
          {[
            ["is_self_employed", "Self-employed"],
            ["is_unemployed", "Unemployed"],
            ["is_student", "Student"],
            ["is_retired", "Retired"],
          ].map(([k, label]) => (
            <label key={k} className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form[k as keyof CreateEmploymentRequest] as boolean}
                onChange={(e) =>
                  set(k as keyof CreateEmploymentRequest, e.target.checked as never)
                }
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      {formError && (
        <p className="text-sm text-red-600">Could not save: {formError}</p>
      )}
      {fieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {saving ? "Saving…" : initial ? "Save changes" : "Add"}
        </button>
      </div>
    </form>
  );
}
